import type {
  EndpointSecurityGlobalPostureSettings,
  GlobalPoliciesDevicePostureFormData,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import type {
  MalwareScanItem,
  NewMalwareScanItem,
} from "@/_store/endpoint-security/malware-scan.module.ts";
import {
  convertRegularPolicyForBackend,
  convertUsbLockdownPolicyForBackend,
  convertUserAccountPolicyForBackend,
  convertWifiPolicyForBackend,
  mapPostureSettings,
} from "@/_store/device-posture/adapters.ts";
import pick from "lodash/pick";
import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";
import defaults from "lodash/defaults";
import {
  DevicePostureOsType,
  DevicePostureSettings,
  DeviceVulnerability,
} from "@/constants/devices.ts";
import type {
  DevicePostureAdaptedItem,
  DevicePostureUsbLockdownItem,
  DevicePostureUserAccountItem,
  DevicePostureUserAccountSettings,
} from "@/_store/device-posture/devices-posture-settings.module.ts";
import { defaultEmptyPolicies, GlobalPolicySection } from "@/constants/msp.ts";

/**
 * Generic type for policies that include an OS type
 */
type PolicyWithOsType = {
  osType?: string;
};

/**
 * Type representing posture settings organized by OS type
 */
type PostureSettings<T> = {
  [osType in DevicePostureOsType]: T[];
};

/**
 * Maps an array of policies to their respective OS types
 * @param policies - Array of policies containing OS type information
 * @param osTypesMap - Mapping of OS type strings to DevicePostureOsType enum values
 * @returns Object with policies grouped by OS type
 */
const mapPoliciesByOsType = <T extends PolicyWithOsType>(
  policies: T[],
  osTypesMap: Record<string, DevicePostureOsType>
): PostureSettings<T> => {
  return Object.entries(osTypesMap).reduce((acc, [key, osType]) => {
    acc[osType] = policies.filter((policy) => policy.osType === key);
    return acc;
  }, {} as PostureSettings<T>);
};

/**
 * Retrieves the unique identifier for a policy, preferring id over temporaryId
 * @param policy - Policy object containing either id or temporaryId
 * @returns The policy's identifier
 */
const getPolicyId = (policy: { id?: string; temporaryId?: string }) =>
  policy.id ?? policy.temporaryId;

/**
 * Converts global device posture settings to adapted items format
 * @param postureSettings - Global endpoint security posture settings
 * @returns Array of adapted device posture items
 */
export const mapGlobalDevicePostureSettings = (
  postureSettings: EndpointSecurityGlobalPostureSettings
): DevicePostureAdaptedItem[] => {
  // Apply default settings if not provided
  const settings = defaults(
    postureSettings,
    defaultEmptyPolicies[GlobalPolicySection.ENDPOINT_SECURITY].devicePosture
  );

  // Extract regular posture settings for Windows and MacOS
  const regularPostureSettings = pick(settings, [
    DevicePostureOsType.WINDOWS,
    DevicePostureOsType.MAC_OS,
  ]);

  const osTypes = {
    windows: DevicePostureOsType.WINDOWS,
    macOs: DevicePostureOsType.MAC_OS,
  };

  // Map user account policies by OS type
  const userAccountPolicySettings: DevicePostureUserAccountSettings =
    mapPoliciesByOsType<DevicePostureUserAccountItem>(settings.userAccountPolicy, osTypes);

  const wifiNetworks = settings.deviceWiFiSettings;

  // Map USB lockdown settings by OS type
  const usbLockdownSettings = mapPoliciesByOsType<DevicePostureUsbLockdownItem>(
    settings.usbLockdownSettings,
    osTypes
  );

  return mapPostureSettings(
    regularPostureSettings,
    userAccountPolicySettings,
    wifiNetworks,
    usbLockdownSettings
  );
};

/**
 * Updates global settings with new device posture form data
 * @param devicePostureFormData - Form data for device posture policy
 * @param currentSettings - Current endpoint security global posture settings
 * @returns Updated endpoint security global posture settings
 */
export const mapFormDataToGlobalSettings = (
  devicePostureFormData: GlobalPoliciesDevicePostureFormData,
  currentSettings: EndpointSecurityGlobalPostureSettings
): EndpointSecurityGlobalPostureSettings => {
  // Clone and apply defaults to current settings
  const settings = cloneDeep(
    defaults(
      currentSettings,
      defaultEmptyPolicies[GlobalPolicySection.ENDPOINT_SECURITY].devicePosture
    )
  );

  // Ensure form data has required IDs
  const formData = cloneDeep({
    ...devicePostureFormData,
    temporaryId: devicePostureFormData.temporaryId || window.crypto.randomUUID(),
    id: devicePostureFormData.id,
  });

  const { policyType, osType } = formData;
  const idToSearch = getPolicyId(formData);

  switch (policyType) {
    case DevicePostureSettings.USB_LOCKDOWN: {
      // Handle USB lockdown policy updates
      const usbPolicy = {
        ...convertUsbLockdownPolicyForBackend(formData),
        temporaryId: formData.temporaryId,
      };
      const existingIndex = settings.usbLockdownSettings.findIndex(
        (policy) => getPolicyId(policy) === idToSearch
      );

      if (existingIndex !== -1) {
        settings.usbLockdownSettings[existingIndex] = usbPolicy;
      } else {
        settings.usbLockdownSettings.push(usbPolicy);
      }
      break;
    }

    case DevicePostureSettings.WIFI: {
      // Handle WiFi policy updates
      const wifiPolicy = {
        ...convertWifiPolicyForBackend(formData),
        temporaryId: formData.temporaryId,
      };
      settings.deviceWiFiSettings[osType] = settings.deviceWiFiSettings[osType] || [];

      const existingIndex = settings.deviceWiFiSettings[osType].findIndex(
        (policy) => getPolicyId(policy) === idToSearch
      );

      if (existingIndex !== -1) {
        settings.deviceWiFiSettings[osType][existingIndex] = wifiPolicy;
      } else {
        settings.deviceWiFiSettings[osType].push(wifiPolicy);
      }
      break;
    }

    case DevicePostureSettings.USER_ACCOUNT_POLICY: {
      // Handle user account policy updates
      const userAccountPolicy = {
        ...convertUserAccountPolicyForBackend(formData),
        temporaryId: formData.temporaryId,
      };
      const existingIndex = settings.userAccountPolicy.findIndex(
        (policy) => getPolicyId(policy) === idToSearch
      );

      if (existingIndex !== -1) {
        settings.userAccountPolicy[existingIndex] = userAccountPolicy;
      } else {
        settings.userAccountPolicy.push(userAccountPolicy);
      }
      break;
    }

    default: {
      // Handle regular policy updates
      const regularPolicy = convertRegularPolicyForBackend(formData, idToSearch);
      const targetArray =
        settings[osType as DevicePostureOsType]?.[policyType as DeviceVulnerability] || [];

      const policyItem =
        regularPolicy[osType as DevicePostureOsType]?.[policyType as DeviceVulnerability]?.[0];
      if (!policyItem) {
        throw new Error(`Invalid policy data for ${osType}/${policyType}`);
      }

      const existingIndex = targetArray.findIndex((policy) => getPolicyId(policy) === idToSearch);

      if (existingIndex !== -1) {
        targetArray[existingIndex] = policyItem;
      } else {
        targetArray.push(policyItem);
      }

      merge(settings, {
        [osType]: {
          [policyType]: targetArray,
        },
      });
      break;
    }
  }

  return settings;
};

/**
 * Removes a policy from global posture settings
 * @param formData - Form data containing policy to remove
 * @param currentSettings - Current endpoint security global posture settings
 * @returns Updated endpoint security global posture settings with policy removed
 */
export const removeFromGlobalPostureSettings = (
  formData: GlobalPoliciesDevicePostureFormData,
  currentSettings: EndpointSecurityGlobalPostureSettings
): EndpointSecurityGlobalPostureSettings => {
  // Clone and apply defaults to current settings
  const settings = cloneDeep(
    defaults(
      currentSettings,
      defaultEmptyPolicies[GlobalPolicySection.ENDPOINT_SECURITY].devicePosture
    )
  );
  const { policyType, osType } = formData;
  const idToSearch = getPolicyId(formData);

  switch (policyType) {
    case DevicePostureSettings.USB_LOCKDOWN: {
      // Remove USB lockdown policy
      settings.usbLockdownSettings = settings.usbLockdownSettings.filter(
        (policy) => getPolicyId(policy) !== idToSearch
      );
      break;
    }

    case DevicePostureSettings.WIFI: {
      // Remove WiFi policy
      if (settings.deviceWiFiSettings[osType]) {
        settings.deviceWiFiSettings[osType] = settings.deviceWiFiSettings[osType].filter(
          (policy) => getPolicyId(policy) !== idToSearch
        );
      }
      break;
    }

    case DevicePostureSettings.USER_ACCOUNT_POLICY: {
      // Remove user account policy
      settings.userAccountPolicy = settings.userAccountPolicy.filter(
        (policy) => getPolicyId(policy) !== idToSearch
      );
      break;
    }

    default: {
      // Remove regular policy
      const targetArray =
        settings[osType as DevicePostureOsType]?.[policyType as DeviceVulnerability];
      if (targetArray) {
        settings[osType as DevicePostureOsType][policyType as DeviceVulnerability] =
          targetArray.filter((policy) => getPolicyId(policy) !== idToSearch);
      }
      break;
    }
  }

  return settings;
};

export const stripIdsFromMalwareScansSchedule = (
  items: MalwareScanItem[] = []
): NewMalwareScanItem[] => items.map(({ id: _id, ...rest }) => rest);
